import type { WithT } from 'i18next';
import debounce from 'lodash.debounce';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Account,
  Campaign,
  Domain,
  EmailVerification,
  EmailVerifications as EmailVerificationsCollection,
  Goal,
  PinpointEmailCampaign,
  Segment,
  Template,
  TFlagsRecord,
} from '@feathr/blackbox';
import { CampaignState, EPinpointRequestStatus, PinpointEmailBaseCampaign } from '@feathr/blackbox';
import type { IWizardStepsCompleted } from '@feathr/components';
import { Step, Steps, useWizardState, Wizard } from '@feathr/components';
import { useAccount, useFlags, useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT, flattenError, flattenErrors, useToggle } from '@feathr/hooks';
import type { Model } from '@feathr/rachis';

import type { ICampaignValidationErrors } from '../../CampaignSummary';
import { ensureEphemeralSegmentsAreSaved } from '../campaignEditUtils';
import CampaignWizardActionBar from '../CampaignWizardActionBar';
import { EmailAudienceConfirmModal } from '../EmailAudienceConfirmModal';
import PendingImportsConfirmModal, {
  usePendingImportsPublishGuard,
} from '../PendingImportsConfirmModal';
import { save } from '../SaveCampaignButton';
import StepGoals, { getGoalSegments, validateStepGoals } from '../StepGoals';
import StepValue, { validateStepValue } from '../StepValue';
import PinpointEmailCampaignStepConfigureDetails, {
  validateStepConfigureDetails,
} from './PinpointEmailCampaignStepConfigureDetails';
import PinpointEmailCampaignStepScheduleSend, {
  validateStepScheduleSend,
} from './PinpointEmailCampaignStepScheduleSend';
import PinpointEmailCampaignStepTest from './PinpointEmailCampaignStepTest';

import * as styles from './PinpointEmailCampaignEdit.css';

interface IProps {
  campaign: PinpointEmailCampaign;
}

export interface IValidatePinpointCampaignProps extends WithT {
  account: Account;
  campaign: PinpointEmailCampaign;
  emailVerification?: EmailVerification;
  goals: IObservableArray<Goal>;
  goalSegments?: Segment[];
  domains?: IObservableArray<Domain>;
  segments?: Segment[];
  template?: Template;
  flags?: Partial<TFlagsRecord>;
}

const getEmailVerifications = debounce(
  (campaign: PinpointEmailCampaign, EmailVerifications: EmailVerificationsCollection) => {
    return campaign.getEmailVerifications(EmailVerifications);
  },
  DEFAULT_DEBOUNCE_WAIT,
  { leading: true },
);

export function getIsVerifiedDomainSender(
  domains: IObservableArray<Domain>,
  emailVerification?: EmailVerification,
  bypassMX?: boolean,
): boolean {
  if (!emailVerification || emailVerification.isEphemeral) {
    /*
     * Return early to ensure we only call .split
     * on a valid email address
     */
    return false;
  }
  const senderDomain = emailVerification.get('email').split('@')[1];
  const verifiedDomains = domains
    .filter((domain) => domain.emailSendingStatus(bypassMX) === EPinpointRequestStatus.Success)
    .map((domain) => domain.get('email_domain'));

  /*
   * Returns true if the sender domain matches or is a subdomain of
   * one of the account's authorized domains
   */
  return verifiedDomains.some((domain) => senderDomain.endsWith(domain));
}

function validate({
  account,
  campaign,
  emailVerification,
  goals,
  goalSegments,
  domains,
  segments,
  template,
  t,
  flags,
}: IValidatePinpointCampaignProps): ICampaignValidationErrors {
  const one = validateStepConfigureDetails(campaign, emailVerification, template);
  const two = campaign.isMonetization
    ? validateStepValue(campaign)
    : validateStepGoals({ goals, goalSegments });
  const three = validateStepScheduleSend(campaign);

  const validationErrors: ICampaignValidationErrors = {
    template: one.template ?? [],
    name: one.name ?? [],
    subject: one.subject ?? [],
    from_address: one.from_address ?? [],
    from_name: one.from_name ?? [],
    'address.company_name': one['address.company_name'] ?? [],
    'address.premise1': one['address.premise1'] ?? [],
    'address.locality': one['address.locality'] ?? [],
    'address.administrative_area_name': one['address.administrative_area_name'] ?? [],
    'address.postal_code': one['address.postal_code'] ?? [],
    'address.country_code': one['address.country_code'] ?? [],
    status: one.status ?? [],
    monetization_value: (two.monetization_value as string[]) ?? [],
    goals: (two.goals as string[]) ?? [],
    segments: three.segments ?? [],
    send_schedule: three.send_schedule ?? [],
    date_send_start: three.date_send_start ?? [],
    'consent.has_consent': three['consent.has_consent'] ?? [],
  };
  if (campaign.isMonetization) {
    validationErrors['monetization_value'] = flattenError(two.monetization_value);
  } else {
    validationErrors['goals'] = flattenError(two.goals);
  }

  if (account.get('email_health') === 'suspended') {
    validationErrors.account = [
      t('Your Feathr account is suspended and cannot publish any new email campaigns.'),
    ];
  }

  if (
    emailVerification &&
    segments &&
    !getIsVerifiedDomainSender(domains!, emailVerification, flags?.bypassMX)
  ) {
    const targetedPeople = segments.reduce((total, segment) => {
      return total + (segment.get('stats_cache')?.num_emails_primary || 0);
    }, 0);

    if (targetedPeople > 1000) {
      validationErrors.contacts_limit = [
        t('You cannot target more than 1,000 contacts unless you authorize your sender domain.'),
      ];
    }
  }

  return validationErrors;
}

function PinpointEmailCampaignEdit({ campaign }: IProps): JSX.Element {
  const account = useAccount();
  const flags = useFlags();
  const { Domains, EmailVerifications, Goals, Segments, Templates } = useStore();
  // Wizard should show loader until first step is determined.
  const [testEmail, setTestEmail] = useState<string | undefined>();
  const [testPersonId, setTestPersonId] = useState<string | undefined>();
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle(false);
  const { t } = useTranslation();

  const domains = Domains.list();

  const goals = Goals.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
  });
  const segments = campaign
    .get('segments', [])
    .filter(({ id }) => !!id)
    .map(({ id }) => Segments.get(id));
  const templateId = campaign.get('template_id');
  const template = templateId ? Templates.get(templateId) : undefined;
  const templates = template ? [template] : [];
  // Used for validation only.
  const emailVerifications = getEmailVerifications(campaign, EmailVerifications);

  const grandchildModels: Model[] = [...getGoalSegments(goals.models, Segments)];

  // Converting observables back to vanilla JavaScript.
  const childModels: Model[] = [...templates, ...segments, ...goals.models.slice()];

  // Pre-compute step errors like DripCampaignEdit
  const goalSegments = getGoalSegments(goals.models, Segments);
  const emailVerification = campaign.getEmailVerification(emailVerifications);

  const stepOneErrors = flattenErrors(
    validateStepConfigureDetails(campaign, emailVerification, template),
  );
  const stepTwoErrors = campaign.isMonetization
    ? flattenErrors(validateStepValue(campaign))
    : flattenErrors(validateStepGoals({ goals: goals.models, goalSegments }));

  const stepFourErrors = flattenErrors(validateStepScheduleSend(campaign));

  const getStepErrors = useCallback(
    (step: number): string[] => {
      // If we're loading the wizard, return no errors
      if (step === -1) {
        return [];
      }
      const map = {
        0: stepOneErrors,
        1: stepTwoErrors,
        // We don't validate the test step because a test send is not necessary to be able to publish a campaign.
        2: [],
        3: stepFourErrors,
      };
      return map[step];
    },
    [campaign, emailVerification, goalSegments, goals.models, template],
  );

  const getCompletedStepMap = useCallback((): IWizardStepsCompleted => {
    return {
      0: !getStepErrors(0).length && !emailVerifications?.isPending,
      1: !getStepErrors(1).length,
      // We don't validate the test step because a test send is not necessary to be able to publish a campaign.
      2: true,
      3: !getStepErrors(3).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailVerifications?.isPending]);
  const waitFor: () => boolean = () => {
    return !(
      campaign.isPending ||
      goals.isPending ||
      // Only check pending status if templateId is set. Template should also not be ephemeral because it's saved as soon as it is selected.
      (campaign.get('template_id') && template && (template?.isEphemeral || template?.isPending))
    );
  };

  const { currentStep, completeStep, lastStep, onNext, onPrev, onChangeStep } = useWizardState({
    getCompletedStepMap,
    waitFor,
    skipToMaxStep: [
      CampaignState.Published,
      CampaignState.Publishing,
      CampaignState.Stopped,
    ].includes(campaign.get('state', CampaignState.Draft)),
    deps: [template],
  });

  function buttonValidate(): ICampaignValidationErrors {
    if (
      !account ||
      !emailVerifications ||
      campaign.isPending ||
      emailVerifications.isPending ||
      goals.isPending
    ) {
      return {};
    }
    const emailVerification = campaign.getEmailVerification(emailVerifications);
    const goalSegments = getGoalSegments(goals.models, Segments);
    return validate({
      account,
      campaign,
      emailVerification,
      goals: goals.models,
      goalSegments,
      domains: domains.models,
      segments,
      t,
      template,
      flags,
    });
  }

  async function preSave(newCampaign: Campaign): Promise<void> {
    await ensureEphemeralSegmentsAreSaved(newCampaign, Segments);
  }

  async function publish(): Promise<void> {
    await save({
      campaign,
      childModels,
      grandchildModels,
      preSave,
      postSave: postStateChange,
      shouldChangeState: true,
      t,
      accountId: account.id,
    });
  }

  const { isPendingImportsModalOpen, closePendingImportsModal, requestPublish, publishAnyway } =
    usePendingImportsPublishGuard(publish);

  async function handleConfirmAudience(): Promise<void> {
    toggleConfirmModal();
    await requestPublish();
  }

  // Set loading while data is fetched and initialized.
  const isLoading =
    campaign.isPending || goals.isPending || emailVerifications?.isPending || currentStep === -1;

  const isDisabled =
    isLoading ||
    campaign.get('state', CampaignState.Draft) === CampaignState.Publishing ||
    !campaign.editable;

  async function postStateChange(campaign: Campaign, stateChanged?: boolean): Promise<void> {
    if (campaign instanceof PinpointEmailBaseCampaign && stateChanged) {
      // The template's read_only field was toggled on the backend, so we must reload it
      await Promise.all(
        templates.map(async (t) => {
          t.reload();
        }),
      );
    }
  }

  const steps = [
    <Step key={1} stepIndex={0} title={t('Configure Details')} />,
    <Step
      key={2}
      stepIndex={1}
      title={campaign.isMonetization ? t('Set Value') : t('Set Goals')}
    />,
    <Step key={3} stepIndex={2} title={t('Test')} />,
    <Step key={4} stepIndex={3} title={t('Schedule & Send')} />,
  ];

  const wizardProps = {
    campaign,
    disabled: campaign.readOnly,
    domains: domains.models,
    goals: goals.models,
    onNext,
    onPrev,
    validate,
    isVerifiedDomainSender: getIsVerifiedDomainSender(
      domains.models,
      campaign.getEmailVerification(emailVerifications),
      flags.bypassMX,
    ),
    validationErrors: buttonValidate(),
  };

  return (
    <div data-appcues-campaign={campaign.get('_cls')}>
      <Wizard
        isFullWidth={true}
        // Set loading while data is fetched and initialized.
        isLoading={completeStep < 0}
        layout={'horizontal'}
        steps={
          <Steps
            completed={completeStep}
            current={currentStep}
            onChange={onChangeStep}
            usePortal={true}
          >
            {steps}
          </Steps>
        }
      >
        <CampaignWizardActionBar
          additionalPublishProps={{
            disabled: isDisabled,
          }}
          additionalSaveProps={{
            disabled: isDisabled,
          }}
          campaign={campaign}
          childModels={childModels}
          currentStep={currentStep}
          getStepErrors={getStepErrors}
          grandchildModels={grandchildModels}
          isLoading={isLoading}
          lastStep={lastStep}
          onNext={onNext}
          onPrev={onPrev}
          onPublishClick={toggleConfirmModal}
          postSave={postStateChange}
          preSave={preSave}
          validate={buttonValidate}
        />

        {/* Email details */}
        {currentStep === 0 && (
          <div className={styles.container}>
            <PinpointEmailCampaignStepConfigureDetails template={template} {...wizardProps} />
          </div>
        )}
        {/* Value or goals, based on if it's a monetization campaign */}
        {currentStep === 1 &&
          (campaign.isMonetization ? (
            <div className={styles.container}>
              <StepValue {...wizardProps} />
            </div>
          ) : (
            <div className={styles.containerWide}>
              <StepGoals {...wizardProps} disabled={false} />
            </div>
          ))}
        {/* Send test email */}
        {currentStep === 2 && (
          <div className={styles.container}>
            <PinpointEmailCampaignStepTest
              {...wizardProps}
              disabled={false}
              setTestEmail={setTestEmail}
              setTestPersonId={setTestPersonId}
              testEmail={testEmail}
              testPersonId={testPersonId}
            />
          </div>
        )}
        {/* Select recipients and schedule send dates/times */}
        {currentStep === 3 && (
          <div className={styles.containerWide}>
            <PinpointEmailCampaignStepScheduleSend {...wizardProps} />
          </div>
        )}
      </Wizard>
      <EmailAudienceConfirmModal
        campaign={campaign}
        onClose={toggleConfirmModal}
        onConfirm={handleConfirmAudience}
        opened={isConfirmModalOpen}
      />
      <PendingImportsConfirmModal
        onClose={closePendingImportsModal}
        onConfirm={publishAnyway}
        opened={isPendingImportsModalOpen}
      />
    </div>
  );
}

export default observer(PinpointEmailCampaignEdit);
